import React, { useState, useEffect } from 'react';
import { Lock, KeyRound, Eye, EyeOff, Shield, AlertTriangle, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const translations = {
  en: {
    title: 'Admin Access',
    subtitle: 'Enter your PIN code to access the administration area',
    pinLabel: 'PIN Code',
    pinPlaceholder: 'Enter 4-6 digit PIN',
    submit: 'Access',
    error: 'Incorrect PIN code. Please try again.',
    logout: 'Logout',
    secured: 'Secured Access',
    locked: 'Access temporarily locked',
    lockedMsg: 'Too many failed attempts. Please wait',
    seconds: 'seconds',
    attemptsLeft: 'attempts remaining',
    securityInfo: 'Protected by encryption and rate limiting'
  },
  fr: {
    title: 'Accès Administration',
    subtitle: 'Entrez votre code PIN pour accéder à l\'espace administration',
    pinLabel: 'Code PIN',
    pinPlaceholder: 'Entrez le PIN à 4-6 chiffres',
    submit: 'Accéder',
    error: 'Code PIN incorrect. Veuillez réessayer.',
    logout: 'Déconnexion',
    secured: 'Accès Sécurisé',
    locked: 'Accès temporairement verrouillé',
    lockedMsg: 'Trop de tentatives échouées. Veuillez patienter',
    seconds: 'secondes',
    attemptsLeft: 'tentatives restantes',
    securityInfo: 'Protégé par cryptage et limitation de débit'
  },
  pt: {
    title: 'Acesso Administração',
    subtitle: 'Digite o código PIN para acessar a área de administração',
    pinLabel: 'Código PIN',
    pinPlaceholder: 'Digite o PIN de 4-6 dígitos',
    submit: 'Acessar',
    error: 'Código PIN incorreto. Por favor, tente novamente.',
    logout: 'Sair',
    secured: 'Acesso Seguro',
    locked: 'Acesso temporariamente bloqueado',
    lockedMsg: 'Muitas tentativas falhadas. Por favor, aguarde',
    seconds: 'segundos',
    attemptsLeft: 'tentativas restantes',
    securityInfo: 'Protegido por encriptação e limitação de taxa'
  },
  de: {
    title: 'Admin-Zugang',
    subtitle: 'Geben Sie Ihren PIN-Code ein, um auf den Verwaltungsbereich zuzugreifen',
    pinLabel: 'PIN-Code',
    pinPlaceholder: 'Geben Sie 4-6 stellige PIN ein',
    submit: 'Zugreifen',
    error: 'Falscher PIN-Code. Bitte versuchen Sie es erneut.',
    logout: 'Abmelden',
    secured: 'Gesicherter Zugang',
    locked: 'Zugang vorübergehend gesperrt',
    lockedMsg: 'Zu viele fehlgeschlagene Versuche. Bitte warten Sie',
    seconds: 'Sekunden',
    attemptsLeft: 'Versuche übrig',
    securityInfo: 'Geschützt durch Verschlüsselung und Ratenbegrenzung'
  },
  es: {
    title: 'Acceso Administración',
    subtitle: 'Ingrese su código PIN para acceder al área de administración',
    pinLabel: 'Código PIN',
    pinPlaceholder: 'Ingrese PIN de 4-6 dígitos',
    submit: 'Acceder',
    error: 'Código PIN incorrecto. Por favor, intente de nuevo.',
    logout: 'Cerrar sesión',
    secured: 'Acceso Seguro',
    locked: 'Acceso temporalmente bloqueado',
    lockedMsg: 'Demasiados intentos fallidos. Por favor espere',
    seconds: 'segundos',
    attemptsLeft: 'intentos restantes',
    securityInfo: 'Protegido por cifrado y limitación de velocidad'
  }
};

const AdminPinGuard = ({ children, onLogout }) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(5);

  // Check if already authenticated in session
  useEffect(() => {
    const sessionAuth = sessionStorage.getItem('admin_authenticated');
    const sessionExpiry = sessionStorage.getItem('admin_auth_expiry');
    
    if (sessionAuth === 'true' && sessionExpiry) {
      const expiry = parseInt(sessionExpiry, 10);
      if (Date.now() < expiry) {
        setIsAuthenticated(true);
      } else {
        // Session expired
        sessionStorage.removeItem('admin_authenticated');
        sessionStorage.removeItem('admin_auth_expiry');
      }
    }
  }, []);

  // Countdown timer for lockout
  useEffect(() => {
    if (lockoutTime > 0) {
      const timer = setInterval(() => {
        setLockoutTime(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [lockoutTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (lockoutTime > 0) return;
    
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API}/admin/verify-pin`, { pin });
      
      if (response.data.success) {
        setIsAuthenticated(true);
        // Set session with 2 hour expiry
        const expiry = Date.now() + (2 * 60 * 60 * 1000);
        sessionStorage.setItem('admin_authenticated', 'true');
        sessionStorage.setItem('admin_auth_expiry', expiry.toString());
        sessionStorage.setItem('admin_pin', pin); // Store PIN for API calls
        setAttemptsLeft(5);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || t.error;
      
      // Check if locked out
      if (err.response?.status === 429) {
        const match = detail.match(/(\d+)/);
        if (match) {
          setLockoutTime(parseInt(match[1], 10));
        }
        setError(t.locked);
      } else {
        // Extract attempts remaining
        const attemptsMatch = detail.match(/(\d+) attempts? remaining/);
        if (attemptsMatch) {
          setAttemptsLeft(parseInt(attemptsMatch[1], 10));
        }
        setError(t.error);
      }
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_authenticated');
    sessionStorage.removeItem('admin_auth_expiry');
    sessionStorage.removeItem('admin_pin'); // Remove stored PIN
    setPin('');
    setAttemptsLeft(5);
    if (onLogout) onLogout();
  };

  if (isAuthenticated) {
    return (
      <div>
        {/* Logout button floating */}
        <div className="fixed top-24 right-4 z-40">
          <Button
            onClick={handleLogout}
            variant="outline"
            className="bg-white/90 backdrop-blur-sm border-kamit-brown/20 text-kamit-brown hover:bg-kamit-gold/10"
            data-testid="admin-logout-btn"
          >
            <Lock className="h-4 w-4 mr-2" />
            {t.logout}
          </Button>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kamit-beige flex items-center justify-center p-4" data-testid="admin-pin-guard">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-kamit-brown p-6 text-center">
            <div className="w-16 h-16 bg-kamit-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="h-8 w-8 text-kamit-gold" />
            </div>
            <h1 className="font-heading text-2xl text-kamit-beige mb-2">
              {t.title}
            </h1>
            <p className="text-kamit-beige/70 text-sm">
              {t.subtitle}
            </p>
          </div>

          {/* Lockout Warning */}
          {lockoutTime > 0 && (
            <div className="bg-red-50 border-b border-red-200 p-4">
              <div className="flex items-center gap-3 text-red-700">
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{t.locked}</p>
                  <p className="text-sm flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    {t.lockedMsg}: <span className="font-mono font-bold">{lockoutTime}</span> {t.seconds}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-kamit-brown mb-2">
                <KeyRound className="inline h-4 w-4 mr-2" />
                {t.pinLabel}
              </label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={pin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setPin(value);
                    setError(null);
                  }}
                  placeholder={t.pinPlaceholder}
                  className={`w-full p-4 pr-12 border rounded-xl text-center text-2xl tracking-widest font-mono transition-colors ${
                    error 
                      ? 'border-red-500 bg-red-50' 
                      : lockoutTime > 0
                      ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                      : 'border-kamit-brown/20 focus:ring-2 focus:ring-kamit-gold focus:border-transparent'
                  }`}
                  maxLength={6}
                  minLength={4}
                  required
                  autoFocus
                  disabled={lockoutTime > 0}
                  data-testid="admin-pin-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-kamit-brown/50 hover:text-kamit-brown"
                >
                  {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              
              {error && (
                <p className="mt-2 text-red-600 text-sm flex items-center gap-1">
                  <Lock className="h-4 w-4" />
                  {error}
                </p>
              )}
              
              {!lockoutTime && attemptsLeft < 5 && (
                <p className="mt-2 text-amber-600 text-sm flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  {attemptsLeft} {t.attemptsLeft}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={loading || pin.length < 4 || lockoutTime > 0}
              className="w-full bg-kamit-gold hover:bg-kamit-gold/90 text-kamit-dark rounded-full py-6 text-lg disabled:opacity-50"
              data-testid="admin-pin-submit"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  ...
                </span>
              ) : (
                <>
                  <Lock className="mr-2 h-5 w-5" />
                  {t.submit}
                </>
              )}
            </Button>
          </form>

          {/* Security Footer */}
          <div className="bg-kamit-beige/50 p-4 text-center space-y-2">
            <p className="text-kamit-brown/60 text-xs flex items-center justify-center gap-1">
              <Shield className="h-3 w-3" />
              {t.secured}
            </p>
            <p className="text-kamit-brown/40 text-xs">
              {t.securityInfo}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPinGuard;
